/**
 * Ingredient-amount scaling for the cocktail multiplier.
 *
 * Honors the §A.4 scale_type contract:
 *   exact          — linear scaling (amount × count)
 *   do_not_scale   — frozen at base amount regardless of count
 *   per_cocktail   — same as exact in MVP (reserved for future per-cocktail logic)
 *   approximate    — same as exact, but UI flags it with "~"
 *   garnish_each   — linear (one unit per cocktail)
 *   batch_only     — hidden from the bartender ingredient list entirely
 */

import type { FinishScaleType, ScaleType } from "../types/drink";

export interface ScaledLine {
  /** Final number to render. 0 if hidden. */
  amount: number;
  /** Final unit string with pluralization applied. */
  unit: string;
  /** True for batch_only — caller should skip rendering. */
  hidden: boolean;
  /** True for approximate scaleType — caller should prefix with "~". */
  approximate: boolean;
}

const PLURAL: Record<string, string> = {
  dash: "dashes",
  drop: "drops",
  spray: "sprays",
  rinse: "rinses",
  pinch: "pinches",
  twist: "twists",
};

/** Reverse of PLURAL for de-pluralizing when amount lands at 1. */
const SINGULAR: Record<string, string> = Object.fromEntries(
  Object.entries(PLURAL).map(([s, p]) => [p, s])
);

/**
 * Pluralize a unit based on amount. Returns the input unit unchanged
 * if no rule applies (e.g., "oz", "ml", "barspoon").
 *
 * Rules:
 *   amount === 1 → singular (dashes → dash)
 *   amount !== 1 → plural   (dash   → dashes)
 */
export function pluralizeUnit(unit: string, amount: number): string {
  if (!unit) return unit;
  const lower = unit.toLowerCase();
  if (amount === 1) {
    if (SINGULAR[lower]) return SINGULAR[lower];
    return unit;
  }
  // amount !== 1
  if (PLURAL[lower]) return PLURAL[lower];
  return unit;
}

/** Format a scaled amount for display.
 *  - Strip trailing zeros: 0.25 → "0.25", 2 → "2", 1.50 → "1.5".
 *  - Snap to an integer when within 0.015 of one — handles scaling artifacts
 *    like 0.33 oz × 3 = 0.99 which should read as "1 oz", not "0.99 oz".
 *  - Use 3-decimal precision when needed (0.625 stays "0.625", not "0.63"). */
export function formatAmount(amount: number): string {
  if (!Number.isFinite(amount)) return String(amount);
  const rounded = Math.round(amount);
  if (Math.abs(amount - rounded) < 0.015) return String(rounded);
  return amount
    .toFixed(3)
    .replace(/\.?0+$/, "");
}

/**
 * Compute the rendered line for one ingredient at a given cocktail count.
 *
 * @example
 *   scaleLine(2, "oz", "exact", 3)    // { amount: 6, unit: "oz", hidden: false, approximate: false }
 *   scaleLine(1, "dash", "exact", 3)  // { amount: 3, unit: "dashes", ... }
 *   scaleLine(2, "dashes", "exact", 1)// { amount: 2, unit: "dashes", ... }  (kept plural — not 1)
 *   scaleLine(2, "dashes", "exact", 0.5) // never happens — count clamped >=1
 *   scaleLine(0.25, "oz", "do_not_scale", 5) // { amount: 0.25, ... }
 *   scaleLine(1, "oz", "batch_only", 3) // { hidden: true, ... }
 */
export function scaleLine(
  baseAmount: number,
  baseUnit: string,
  scaleType: ScaleType,
  cocktailCount: number
): ScaledLine {
  if (scaleType === "batch_only") {
    return { amount: 0, unit: baseUnit, hidden: true, approximate: false };
  }
  let amount = baseAmount;
  if (scaleType === "do_not_scale") {
    // amount unchanged
  } else {
    // exact, per_cocktail, approximate, garnish_each — all linear in MVP
    amount = baseAmount * cocktailCount;
  }
  const unit = pluralizeUnit(baseUnit, amount);
  return {
    amount,
    unit,
    hidden: false,
    approximate: scaleType === "approximate",
  };
}

/** Format the amount + unit for display, with optional "~" prefix for approximate. */
export function renderLine(line: ScaledLine): string {
  if (line.hidden) return "";
  const prefix = line.approximate ? "~" : "";
  return `${prefix}${formatAmount(line.amount)} ${line.unit}`.trim();
}

/**
 * Per-row scaling for Finish / Presentation steps.
 *
 *   per_cocktail  — multiplied by cocktail count ("3× Express orange peel")
 *   do_not_scale  — singular regardless of count ("Smoke chamber")
 *   optional      — same shape as per_cocktail; UI renders muted/secondary
 *   batch_finish  — singular regardless of count, with explicit "(once for the round)" hint
 */
export interface ScaledFinish {
  /** How many times this step happens this round; 1 means "once". */
  multiplier: number;
  /** True when the row is rendered de-emphasized (optional). */
  optional: boolean;
  /** True for batch_finish (caller can render a "single batch" hint). */
  batch: boolean;
  /** True when scaling resolved to do_not_scale. */
  doNotScale: boolean;
}

export function scaleFinish(
  scaleType: FinishScaleType,
  cocktailCount: number
): ScaledFinish {
  switch (scaleType) {
    case "do_not_scale":
      return { multiplier: 1, optional: false, batch: false, doNotScale: true };
    case "batch_finish":
      return { multiplier: 1, optional: false, batch: true, doNotScale: false };
    case "optional":
      return { multiplier: Math.max(1, cocktailCount), optional: true, batch: false, doNotScale: false };
    case "per_cocktail":
    default:
      return { multiplier: Math.max(1, cocktailCount), optional: false, batch: false, doNotScale: false };
  }
}

/**
 * Build the rendered prefix for a finish-step label given its scale.
 * Examples:
 *   per_cocktail @ 1x → ""        (no prefix; label stands alone)
 *   per_cocktail @ 3x → "3× "
 *   optional     @ 2x → "2× "
 *   do_not_scale     → ""
 *   batch_finish     → ""         (caller adds a hint after the label)
 */
export function finishPrefix(scaled: ScaledFinish): string {
  if (scaled.doNotScale || scaled.batch) return "";
  if (scaled.multiplier <= 1) return "";
  return `${scaled.multiplier}× `;
}
